import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { alpha, Theme } from '@mui/material/styles'

// Widths of the three columns of the PO matching layout: the invoice data, the
// connector lines between the two sides, and the purchase orders.
export const INVOICE_PANE_WIDTH = '45%'
export const CONNECTOR_PANE_WIDTH = '10%'
export const PURCHASE_ORDER_PANE_WIDTH = '45%'

type FormatValueOptions = {
  decimals?: number
  prefix?: string
}

/** Mirrors pd-po's `normalize_po_number` so client and server agree on what counts as the same PO number. */
export function normalizePoNumber(value: string | null | undefined): string {
  if (!value) return ''

  let normalized = value.trim().toUpperCase()
  normalized = normalized.replace(/^(PO|P\.O\.?)\s*[-#:.]?\s*/, '')
  normalized = normalized.replace(/^[ \-#:.]+|[ \-#:.]+$/g, '')
  normalized = normalized.replace(/^0+/, '')
  return normalized
}

/** Two PO numbers match when both normalize to the same non-empty value. */
export function poNumbersMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const normalizedA = normalizePoNumber(a)
  const normalizedB = normalizePoNumber(b)
  if (!normalizedA || !normalizedB) return false
  return normalizedA === normalizedB
}

/** Formats a numeric string with optional fixed decimals and prefix (e.g. currency symbol). */
export function formatValue(
  value: string | null,
  { decimals, prefix = '' }: FormatValueOptions = {},
): string {
  if (!value) return '—'

  const parsed = parseFloat(value)
  if (isNaN(parsed)) return value

  if (decimals === undefined) {
    return `${prefix}${parsed.toLocaleString('en-US')}`
  }
  return `${prefix}${parsed.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

function toNumber(value: string | null | undefined): number {
  const parsed = parseFloat(value ?? '')
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Whether a purchase order line was ordered as money with no units to spend it on.
 *
 * Keep this in sync with the same check in pd-po and py-zrelay, so the screen and the export
 * engine agree on which lines count as lump sum. It looks only at the quantity and amount the
 * line was originally ordered for, not the remaining quantity and amount. Those drop as
 * invoices get matched against the line, and a lump sum line that has been fully billed still
 * needs to count as a lump sum line.
 */
export function isLumpSumPoLine(
  orderedQuantity: string | null | undefined,
  orderedAmount: string | null | undefined,
): boolean {
  return toNumber(orderedQuantity) === 0 && toNumber(orderedAmount) > 0
}

export function hidesLumpSumRate(
  isLumpSum: boolean,
  rate: string | null | undefined,
): boolean {
  return isLumpSum && toNumber(rate) === 0
}

type LineItemWithJob = {
  jobNumber: string | null
  phaseNumber: string | null
}

function getSharedLineItemNumber(
  rows: LineItemWithJob[],
  key: 'jobNumber' | 'phaseNumber',
): string | null | undefined {
  if (rows.length === 0) return null

  const normalizedNumbers = rows.map((row) => row[key]?.trim() || null)
  const firstNumber = normalizedNumbers[0]

  return normalizedNumbers.every((num) => num === firstNumber)
    ? firstNumber
    : undefined
}

export function getSharedLineItemJob(
  rows: LineItemWithJob[],
): string | null | undefined {
  return getSharedLineItemNumber(rows, 'jobNumber')
}

export function getSharedLineItemPhase(
  rows: LineItemWithJob[],
): string | null | undefined {
  return getSharedLineItemNumber(rows, 'phaseNumber')
}

export type DiscrepancySeverity = 'warning' | 'error'

/**
 * A field/cell discrepancy: its severity (warning vs error) and the tooltip to
 * show. Shared by `FieldItem`, the line-item cells, and the response mapping.
 */
export type FieldDiscrepancy = {
  severity: DiscrepancySeverity
  tooltip: string
}

/** The shared tinted-background treatment for any field, cell, or row with a discrepancy. */
export function discrepancyBgColor(
  severity: DiscrepancySeverity = 'warning',
  opacity = 0.2,
) {
  return (theme: Theme) =>
    alpha(
      severity === 'error'
        ? theme.palette.error.main
        : theme.palette.warning.main,
      opacity,
    )
}

/**
 * The rounded, tinted "pill" treatment shared by discrepancy fields (`FieldItem`)
 * and discrepancy line-item cells (`DiscrepancyPill`). The negative margins cancel
 * the padding so marking a field/cell never changes surrounding layout (which would
 * shift the connector dots measured against the cards). `emphasized` is the hover
 * treatment: a stronger tint plus a ring.
 */
export function discrepancyPillSx(
  severity: DiscrepancySeverity,
  emphasized: boolean,
) {
  return {
    bgcolor: discrepancyBgColor(severity, emphasized ? 0.45 : 0.2),
    boxShadow: emphasized
      ? (theme: Theme) =>
          `0 0 0 1.5px ${
            severity === 'error'
              ? theme.palette.error.main
              : theme.palette.warning.main
          }`
      : undefined,
    borderRadius: 1,
    px: 0.75,
    py: 0.25,
    mx: -0.75,
    my: -0.25,
    transition: 'background-color 150ms ease, box-shadow 150ms ease',
  }
}

type FieldItemProps = {
  label: string
  value: string | null
  mono?: boolean
  tooltip?: string
  /** When set, renders a tinted background + warning icon and uses the discrepancy's tooltip instead of `tooltip`. */
  discrepancy?: FieldDiscrepancy
  /** Renders the field with a discrepancy in the emphasized (hover) treatment — used to mirror a hover on its counterpart field. */
  discrepancyEmphasized?: boolean
  /** Fired when the pointer enters/leaves a field with a discrepancy. Never fires without a discrepancy. */
  onDiscrepancyHoverChange?: (hovering: boolean) => void
  action?: React.ReactNode
}

export function FieldItem({
  label,
  value,
  mono,
  tooltip = '',
  discrepancy,
  discrepancyEmphasized,
  onDiscrepancyHoverChange,
  action,
}: FieldItemProps) {
  const effectiveTooltip = discrepancy ? discrepancy.tooltip : tooltip

  return (
    <Tooltip
      title={effectiveTooltip}
      disableHoverListener={!effectiveTooltip}
      enterDelay={200}
      disableInteractive
      arrow
      placement="top"
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ minWidth: 0 }}
      >
        <Box
          data-discrepancy-severity={discrepancy?.severity}
          data-emphasized={
            discrepancy && discrepancyEmphasized ? 'true' : undefined
          }
          onMouseEnter={
            discrepancy && onDiscrepancyHoverChange
              ? () => onDiscrepancyHoverChange(true)
              : undefined
          }
          onMouseLeave={
            discrepancy && onDiscrepancyHoverChange
              ? () => onDiscrepancyHoverChange(false)
              : undefined
          }
          sx={{
            minWidth: 0,
            ...(discrepancy &&
              discrepancyPillSx(discrepancy.severity, !!discrepancyEmphasized)),
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.2, mb: 0.25 }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={500}
            noWrap
            sx={mono ? { fontFamily: 'monospace' } : undefined}
          >
            {value || '—'}
          </Typography>
        </Box>
        {action}
      </Stack>
    </Tooltip>
  )
}
